import { apiClient } from '@/shared/api/apiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import z from 'zod'

const PRECURSOR_EMISSION_REPORTS_QUERY_KEY = 'precursor-emission-reports'

export type PrecursorEmissionReport = {
  id: string
  cnCode: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  goodsDescription: string
  proofFilePath: string | null
  specificDirectEmbeddedEmissions: number
  specificIndirectEmbeddedEmissions: number
  specificElectricityConsumption: number
  startDate: string
  endDate: string
  precursorSupplierId: string
  useDefaultEmissionValues: boolean
  precursorSupplier: {
    id: string
    name: string
    country: string
  }
}

export const useGetPrecursorEmissionReports = (organizationId: string) => {
  return useQuery<PrecursorEmissionReport[]>({
    queryKey: [PRECURSOR_EMISSION_REPORTS_QUERY_KEY],
    queryFn: async () => {
      const res = await apiClient.get<PrecursorEmissionReport[]>(
        `/v2/organizations/${organizationId}/precursor-emission-reports`
      )
      return res.data
    }
  })
}

type CreatePrecursorEmissionReportPayload = {
  precursorSupplierId: string
  startDate: string
  endDate: string
  cnCode: string
  goodsDescription: string
  specificDirectEmbeddedEmissions: number
  specificIndirectEmbeddedEmissions: number
  specificElectricityConsumption: number
  proofFilePath?: string
  useDefaultEmissionValues: boolean
}

export const createPrecursorEmissionReportPayloadSchema = z.object({
  precursorSupplierId: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  cnCode: z.string().min(1),
  goodsDescription: z.string().min(1),
  specificDirectEmbeddedEmissions: z.number(),
  specificIndirectEmbeddedEmissions: z.number(),
  specificElectricityConsumption: z.number(),
  proofFilePath: z.string().optional(),
  useDefaultEmissionValues: z.boolean()
})

export const useCreatePrecursorEmissionReport = (organizationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreatePrecursorEmissionReportPayload) => {
      const res = await apiClient.post<PrecursorEmissionReport>(
        `/v2/organizations/${organizationId}/precursor-emission-reports`,
        payload
      )
      return res.data
    },
    onSuccess: data => {
      queryClient.setQueryData(
        [PRECURSOR_EMISSION_REPORTS_QUERY_KEY],
        (old: PrecursorEmissionReport[]) => {
          return [...old, data]
        }
      )
    }
  })
}
